import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl';
import { formatInTimeZone } from 'date-fns-tz';

const CameraLayer = ({
    layerId,
    visible,
    currentDateTime,
}) => {
    const { current: map } = useMap();
    const [cameraData, setCameraData] = useState([]);
    const [selectedCamera, setSelectedCamera] = useState(null);
    const [dataFetched, setDataFetched] = useState(false); // State to track data fetching

    // Fetch the camera coordinates from the backend
    const fetchCameraData = useCallback(async () => {
        if (!visible || dataFetched) return;  // Only fetch if not already fetched and layer is visible
        try {
            const response = await fetch('https://globalmeteornetwork.org/data/kml_fov/GMN_station_coordinates_public.json');
            if (!response.ok) {
                throw new Error('Failed to fetch camera data');
            }
            const data = await response.json();
            setCameraData(data);
            setDataFetched(true);
        } catch (error) {
            console.error('Error fetching camera data:', error);
        }
    }, [visible, dataFetched]);

    useEffect(() => {
        fetchCameraData();
    }, [fetchCameraData]);

    // Function to find the latest camera position based on current date-time
    const getLatestCameraData = (cameraId) => {
        
        const cameraTimes = cameraData[cameraId];
        const cameraEntries = Object.entries(cameraTimes || {});
        
        const validEntries = cameraEntries.filter(([timestamp]) => new Date(timestamp) <= new Date(currentDateTime));
        validEntries.sort(([timestampA], [timestampB]) => new Date(timestampB) - new Date(timestampA));
        return validEntries.length > 0 ? validEntries[0][1] : null;
    };

    // Convert camera data into GeoJSON format
    const cameraFeatures = useMemo(() => {
        const features = [];
        //console.log(cameraData);
        for (const [cameraId, cameraTimes] of Object.entries(cameraData)) {
            const latestCameraData = getLatestCameraData(cameraId);
            if (latestCameraData) {
                features.push({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [latestCameraData.lon, latestCameraData.lat]
                    },
                    properties: {
                        ...latestCameraData,
                        name: cameraId
                    }
                });
            }
        }
        return features;
    }, [cameraData, currentDateTime]);

    // Define camera layer style
    const cameraLayer = useMemo(() => ({
        id: `${layerId}-camera-layer`,
        type: 'circle',
        paint: {
            'circle-radius': 2.5,
            'circle-color': 'rgba(0, 255, 0, 0.7)',
            'circle-stroke-width': 1.5,
            'circle-stroke-color': 'white'
        },
        layout: {
            visibility: visible ? 'visible' : 'none'
        }
    }), [layerId, visible]);

    // Function to handle click on a camera marker
    const handleClick = (e) => {
        const feature = e.features[0];
        const cameraInfo = feature.properties;
        setSelectedCamera(cameraInfo);
    };

    // Attach the click event listener to the map
    useEffect(() => {
        if (!map) return;

        map.on('click', `${layerId}-camera-layer`, handleClick);

        return () => {
            if (map) {
                map.off('click', `${layerId}-camera-layer`, handleClick);
            }
        };
    }, [map, layerId]);

    const renderPointingData = (pointing) => {
        if (!pointing) return null;
        const pointingString = JSON.stringify(pointing);
        const pointingEntries = pointingString
            .replace(/[{}"]/g, '')
            .split(',')
            .map((entry) => entry.split(':').map((part) => part.trim()));

        return pointingEntries.map(([key, value], index) => (
            <p key={index}><strong>{key.replace(/\\/g, '')}:</strong> {value}</p>
        ));
    };

    const renderPopupContent = () => {
        if (!selectedCamera) return null;
        return (
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                <h3>{selectedCamera.name}</h3>
                <p><strong>lat:</strong> {selectedCamera.lat}</p>
                <p><strong>lon:</strong> {selectedCamera.lon}</p>
                <p><strong>ht (m):</strong> {selectedCamera.ht}</p>
                {renderPointingData(selectedCamera.pointing)}
            </div>
        );
    };

    return (
        <>
            {cameraFeatures.length > 0 && (
                <Source id={`${layerId}-camera-source`} type="geojson" data={{ type: 'FeatureCollection', features: cameraFeatures }}>
                    <Layer {...cameraLayer} />
                </Source>
            )}
            {selectedCamera && (
                <Popup
                    longitude={parseFloat(selectedCamera.lon)}
                    latitude={parseFloat(selectedCamera.lat)}
                    onClose={() => setSelectedCamera(null)}
                    closeButton={true}
                    closeOnClick={true}
                    maxWidth="300px"
                >
                    {renderPopupContent()}
                </Popup>
            )}
        </>
    );
};

export default CameraLayer;
